<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button
            class="backButton"
            @click="onBackButtonClick"
            v-tooltip="{
              content: `${$t('master.tooltipCommon.back')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <span class="tti-viewTitle">
            <!-- エリア別分析 -->
            <tti-svg-icon icon-name="wo_012"></tti-svg-icon>
            {{ $t('operationalPerformance.analysisByAreaTitle') }}
            <!-- グラフ表示(エリア別) -->
            <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
            <tti-svg-icon icon-name="wo_016"></tti-svg-icon>
            {{ $t('operationalPerformance.showGraphArea') }}
          </span>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title">
        <span class="contentTitle">{{ $t('operationalPerformance.areaOccupancy') }}</span>
      </div>
      <ttiCharts
        class="charts"
        :data="moveResult"
        @onChartContextClick="onChartContextClick"
        height="calc(100vh - 48px)"
      ></ttiCharts>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 印刷 -->
        <el-button
          class="tti-btn-default tti-btn-002"
          @click="onPdfBtnClick"
          v-tooltip="{
            content: `${$t('operationalPerformance.printing')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          <tti-svg-icon icon-name="he_008"></tti-svg-icon>
          {{ $t('buttons.print') }}
        </el-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import { AxiosResponse } from 'axios'

import { i18n } from '@/locales'
import { msgBoxService } from '@/commons/services/msgBoxService'
import HelperService from '@/service/helperService'
import ttiCharts from '@/components/ttiCharts.vue'
import {
  MoveResult,
  ResponseData,
  PrefectureAllocData,
  ChartRouteQuery,
  AreaMasterData,
  MoveResultItem
} from '@/models'
import { getAreaMasterDataWhoHasSubPrefecture, opPerfAreaPDFDownload, getOpPerfArea } from '@/store/apis'
import config from '@/config/config'
import { changeBlobToJson } from '@/store/apis/apiClient'
import { AreaRole, DeleteType } from '@/commons/constants'
import helperService from '@/service/helperService'
import { correctTimeFormat, isDateAfter, isDateDayOver } from '@/utils/dateUtils'

/**
 * エリア画面
 *
 * @class
 */
@Component({
  name: 'chart-area',
  components: { ttiCharts }
})
export default class CHART_AREA extends Vue {
  /**
   * エリアルーティングパラメータ定義
   */
  chartRouteQuery: ChartRouteQuery = {
    areaIdList: [],
    operationStartDay: '',
    operationEndDay: '',
    deleteFlg: ''
  }

  /**
   * 稼働実績データ
   */
  moveResult: MoveResult = {
    moveResult: []
  }

  /**
   * エリア情報
   */
  areaMasterData: AreaMasterData[] = []

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('CHART_AREA > mounted')
    this.initArea()
    if (!this.routeQueryCheck()) {
      this.movepage()
      return
    }
    await this.fetchAreaOptions()
    this.areaNotExistProxy(this.handleShowGraph)
  }

  /**
   * URLからエリア取得
   *
   * @returns {void}
   */
  initArea(): void {
    this.chartRouteQuery = {
      areaIdList: this.$route.query.areaIdList as [],
      operationStartDay: this.$route.query.operationStartDate as string,
      operationEndDay: this.$route.query.operationEndDate as string,
      deleteFlg: this.$route.query.deleteFlg as string
    }
  }

  /**
   * パラメータ着信がない場合
   *
   * @returns {boolean} 関連結菓
   */
  routeQueryCheck(): boolean {
    if (
      !this.chartRouteQuery.areaIdList ||
      this.chartRouteQuery.areaIdList.length === 0 ||
      !this.chartRouteQuery.operationStartDay ||
      !this.chartRouteQuery.operationEndDay
    ) {
      return false
    }

    if (
      this.chartRouteQuery.deleteFlg !== DeleteType.notdeleteData &&
      this.chartRouteQuery.deleteFlg !== DeleteType.deleteData
    ) {
      return false
    }

    if (
      !correctTimeFormat(this.chartRouteQuery.operationStartDay) ||
      !correctTimeFormat(this.chartRouteQuery.operationEndDay) ||
      isDateAfter(this.chartRouteQuery.operationStartDay, this.chartRouteQuery.operationEndDay) ||
      isDateDayOver(this.chartRouteQuery.operationEndDay, this.chartRouteQuery.operationStartDay, 31)
    ) {
      return false
    }

    return true
  }

  /**
   * パラメータが間違っている場合の処理
   */
  movepage(): void {
    msgBoxService.info(i18n.t('vaildate.common.E00092').toString())
    this.$router.push(config.menu.areaAnalysis)
  }

  /**
   * エリア情報を取得する
   *
   * @returns {void}
   */
  async fetchAreaOptions(): Promise<void> {
    const data = (await getAreaMasterDataWhoHasSubPrefecture()) as PrefectureAllocData
    this.areaMasterData = data.areaList
    this.$log.debug('CHART_AREA > fetchAreaOptions', data)
  }

  /**
   * 戻るボタンをクリックした時の処理
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    window.history.back()
    this.$log.debug('CHART_AREA > onBackButtonClick')
  }

  /**
   * APIを呼び出すときに、エリアが存在しない可能性があります、これらのAPIをプロキシする。
   *
   * @param {any} target プロキシの目標処理
   * @returns {Promise<void>}
   */
  // eslint-disable-next-line
  async areaNotExistProxy(target: any): Promise<void> {
    try {
      await target()
    } catch (e) {
      this.$log.debug('CHART_AREA > areaNotExistProxy: e=', e)
      const err = e as AxiosResponse<ResponseData>
      // エリアが存在しない場合、エリア別分析画面へ遷移する。
      changeBlobToJson(err).then(commonResponse => {
        if (commonResponse.common.code === 'E00091') {
          this.$router.push(config.menu.areaAnalysis)
        }
      })
      throw e
    }
  }

  /**
   * グラフ表示処理
   *
   * @returns {void}
   */
  async handleShowGraph(): Promise<void> {
    if (typeof this.chartRouteQuery.areaIdList === 'string') {
      this.chartRouteQuery.areaIdList = [this.chartRouteQuery.areaIdList]
    }
    const param = {
      areaIdList: this.chartRouteQuery.areaIdList,
      operationStartDate: this.chartRouteQuery.operationStartDay,
      operationEndDate: this.chartRouteQuery.operationEndDay,
      deleteFlg: this.chartRouteQuery.deleteFlg
    }

    const masters = await getOpPerfArea(param)
    this.moveResult.moveResult = masters.areaList.map(item => {
      return {
        id: item.areaId,
        label: this.findResultAreaName(item.areaId),
        result: (item.operatingRate * 100).toFixed(1)
      } as MoveResultItem
    })
  }

  /**
   * エリアリストからエリア名を取得する
   *
   * @param {string} id  エリアID
   * @returns {void}
   */
  findResultAreaName(id: string): string {
    return helperService.getDefinitionName(this.areaMasterData, id, {
      code: 'id',
      value: 'areaName'
    })
  }

  /**
   * グラフをクリックしてジャンプ
   *
   * @param {any} result クリックしたデータ
   * @returns {void}
   */
  // eslint-disable-next-line
  async onChartContextClick(result: any): Promise<void> {
    this.$router.push({
      path: config.menu.chartPrefecture,
      query: {
        level: AreaRole.AREA,
        label: result.moveResult.label,
        areaId: result.moveResult.id,
        operationStartDate: this.chartRouteQuery.operationStartDay,
        operationEndDate: this.chartRouteQuery.operationEndDay,
        deleteFlg: this.chartRouteQuery.deleteFlg
      }
    })
  }

  onPdfBtnClick(): void {
    try {
      const confirmCallback = () => {
        this.areaNotExistProxy(this.areaPDFDownloadApi)
      }
      msgBoxService.confirm(i18n.t('operationalPerformance.I00160').toString(), confirmCallback)
    } catch (e) {
      this.$log.debug('CHART_AREA > onPdfBtnClick: e=', e)
      const err = e as AxiosResponse<ResponseData>
      // エリアが存在しない場合、エリア別分析画面へ遷移する。
      changeBlobToJson(err).then(commonResponse => {
        if (commonResponse.common.code === 'E00091') {
          this.$router.push(config.menu.areaAnalysis)
        }
      })
      throw e
    }
  }

  async areaPDFDownloadApi(): Promise<void> {
    const params = {
      areaIdList: this.chartRouteQuery.areaIdList,
      operationStartDate: this.chartRouteQuery.operationStartDay,
      operationEndDate: this.chartRouteQuery.operationEndDay,
      deleteFlg: this.chartRouteQuery.deleteFlg
    }

    const res = await opPerfAreaPDFDownload(params)
    HelperService.download(res.fileName, res.data)
  }
}
</script>

<style lang="scss" scoped>
.tti-dialog-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: #909399;

  .back-icon {
    cursor: pointer;
    padding: 0 15px 5px 0;
    color: #909399;
  }
}

.tti-padding-content {
  background-color: #f2f2f2;

  .charts {
    background-color: #fff;
  }
}
</style>
